import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  TrendingUp,
  People,
  Email,
  ShoppingCart,
  Timeline,
  BarChart,
  Refresh,
  Download,
  Person,
  Event,
  DeviceHub,
  Speed,
  Psychology,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import * as Recharts from "recharts";

interface AnalyticsData {
  users: {
    total: number;
    new: number;
    active: number;
    conversionRate: number;
  };
  events: {
    total: number;
    topEvents: Array<{ event: string; count: number }>;
    recentEvents: Array<{
      id: string;
      event: string;
      userId: string;
      timestamp: string;
      properties: any;
    }>;
  };
  funnel: {
    stages: Array<{ stage: string; count: number; conversionRate: number }>;
  };
  activities: {
    total: number;
    popular: Array<{ title: string; views: number }>;
    categories: Array<{ category: string; count: number }>;
  };
  performance: {
    avgResponseTime: number;
    errorRate: number;
    uptime: number;
  };
}

interface TopEvent {
  event: string;
  count: number;
}

interface PieChartData {
  name?: string;
  category?: string;
  percent?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AnalyticsDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState("7d");
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard stats
      const statsResponse = await fetch(
        "http://localhost:3001/api/analytics/dashboard"
      );
      const statsData = await statsResponse.json();

      // Fetch funnel data
      const funnelResponse = await fetch(
        "http://localhost:3001/api/analytics/funnel"
      );
      await funnelResponse.json(); // We're not using this data yet

      // Fetch recent events
      const eventsResponse = await fetch(
        "http://localhost:3001/api/analytics/journey"
      );
      const eventsData = await eventsResponse.json();

      // Use real API data
      const realData: AnalyticsData = {
        users: {
          total: statsData.stats.totalUsers,
          new: statsData.stats.newUsersToday,
          active: statsData.stats.activeUsers,
          conversionRate: statsData.stats.conversionRate,
        },
        events: {
          total: statsData.stats.totalEvents,
          topEvents: statsData.stats.topEvents,
          recentEvents: eventsData.journey || [],
        },
        funnel: {
          stages: [
            {
              stage: "Landing Page Visit",
              count: statsData.stats.totalUsers * 10,
              conversionRate: 100,
            },
            {
              stage: "Email Capture",
              count:
                statsData.stats.topEvents.find(
                  (e: TopEvent) => e.event === "email_captured"
                )?.count || 0,
              conversionRate: statsData.stats.conversionRate,
            },
            {
              stage: "User Registration",
              count: statsData.stats.totalUsers,
              conversionRate: 100,
            },
            {
              stage: "Subscription",
              count: Math.floor(
                (statsData.stats.topEvents.find(
                  (e: TopEvent) => e.event === "email_captured"
                )?.count || 0) * 0.1
              ),
              conversionRate: 10,
            },
          ],
        },
        activities: {
          total: 10, // From our seeded data
          popular: [
            { title: "Backyard Treasure Hunt", views: 234 },
            { title: "Kitchen Science Experiments", views: 189 },
            { title: "Indoor Camping Adventure", views: 156 },
            { title: "DIY Robot Building", views: 123 },
          ],
          categories: [
            { category: "Outdoor Activities", count: 3 },
            { category: "Science & Learning", count: 3 },
            { category: "Arts & Crafts", count: 2 },
            { category: "Physical Activities", count: 1 },
            { category: "Educational Games", count: 1 },
          ],
        },
        performance: {
          avgResponseTime: 245,
          errorRate: 0.8,
          uptime: 99.9,
        },
      };

      setData(realData);
    } catch (err) {
      setError("Failed to fetch analytics data");
      // console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchAnalyticsData, 30000);
    setRefreshInterval(interval);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [timeRange, refreshInterval]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  const handleExport = () => {
    // Export functionality
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `analytics-${new Date().toISOString().split("T")[0]}.json`;
    link.click();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchAnalyticsData}>
          Retry
        </Button>
      </Container>
    );
  }

  if (!data) return null;

  const chartColors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#ff0000"];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            <BarChart sx={{ mr: 2, verticalAlign: "middle" }} />
            Analytics Dashboard
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="1d">Last 24h</MenuItem>
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Real-time insights into user behavior, conversions, and platform
          performance
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <People sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.users.total.toLocaleString()}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TrendingUp
                    sx={{ color: "success.main", mr: 1, fontSize: 16 }}
                  />
                  <Typography variant="body2" color="success.main">
                    +{data.users.new} new today
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Email sx={{ color: "secondary.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Email Captures
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.events.topEvents
                    .find((e: TopEvent) => e.event === "email_captured")
                    ?.count.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.users.conversionRate}% conversion rate
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ShoppingCart sx={{ color: "warning.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Subscriptions
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.events.topEvents
                    .find((e: TopEvent) => e.event === "subscription_created")
                    ?.count.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.funnel.stages[3].conversionRate}% conversion rate
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Speed sx={{ color: "info.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Avg Response
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.performance.avgResponseTime}ms
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.performance.uptime}% uptime
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="analytics tabs"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Overview" />
          <Tab label="User Journey" />
          <Tab label="Activities" />
          <Tab label="Performance" />
          <Tab label="Real-time Events" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Conversion Funnel */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Conversion Funnel
                  </Typography>
                  <Recharts.ResponsiveContainer width="100%" height={300}>
                    <Recharts.BarChart data={data.funnel.stages}>
                      <Recharts.CartesianGrid strokeDasharray="3 3" />
                      <Recharts.XAxis dataKey="stage" />
                      <Recharts.YAxis />
                      <Recharts.Tooltip />
                      <Recharts.Bar dataKey="count" fill="#8884d8" />
                    </Recharts.BarChart>
                  </Recharts.ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Event Distribution */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Event Distribution
                  </Typography>
                  <Recharts.ResponsiveContainer width="100%" height={300}>
                    <Recharts.PieChart>
                      <Recharts.Pie
                        data={data.events.topEvents}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }: PieChartData) =>
                          `${name} ${((percent || 0) * 100).toFixed(0)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {data.events.topEvents.map((entry, index) => (
                          <Recharts.Cell
                            key={`cell-${index}`}
                            fill={chartColors[index % chartColors.length]}
                          />
                        ))}
                      </Recharts.Pie>
                      <Recharts.Tooltip />
                    </Recharts.PieChart>
                  </Recharts.ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Categories */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Activity Categories
                  </Typography>
                  <Grid container spacing={2}>
                    {data.activities.categories.map((category, index) => (
                      <Grid item xs={12} sm={6} md={4} key={category.category}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2">
                            {category.category}
                          </Typography>
                          <Chip
                            label={category.count}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            (category.count /
                              Math.max(
                                ...data.activities.categories.map(
                                  (c) => c.count
                                )
                              )) *
                            100
                          }
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* User Journey Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    User Journey Timeline
                  </Typography>
                  <Recharts.ResponsiveContainer width="100%" height={400}>
                    <Recharts.AreaChart data={data.funnel.stages}>
                      <Recharts.CartesianGrid strokeDasharray="3 3" />
                      <Recharts.XAxis dataKey="stage" />
                      <Recharts.YAxis />
                      <Recharts.Tooltip />
                      <Recharts.Area
                        type="monotone"
                        dataKey="count"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.3}
                      />
                    </Recharts.AreaChart>
                  </Recharts.ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Conversion Rates */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Conversion Rates
                  </Typography>
                  {data.funnel.stages.map((stage, index) => (
                    <Box key={stage.stage} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">{stage.stage}</Typography>
                        <Typography variant="body2" color="primary">
                          {stage.conversionRate}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stage.conversionRate}
                        color={
                          stage.conversionRate > 50
                            ? "success"
                            : stage.conversionRate > 25
                            ? "warning"
                            : "error"
                        }
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* User Demographics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    User Demographics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary="Active Users"
                        secondary={`${data.users.active.toLocaleString()} users`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <Event />
                      </ListItemIcon>
                      <ListItemText
                        primary="New Users (Today)"
                        secondary={`${data.users.new} users`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <Psychology />
                      </ListItemIcon>
                      <ListItemText
                        primary="Conversion Rate"
                        secondary={`${data.users.conversionRate}%`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Activities Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Popular Activities
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Activity</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">Engagement</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.activities.popular.map((activity, index) => (
                          <TableRow key={activity.title}>
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Chip
                                  label={index + 1}
                                  size="small"
                                  color="primary"
                                  sx={{ mr: 2 }}
                                />
                                {activity.title}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {activity.views}
                            </TableCell>
                            <TableCell align="right">
                              <LinearProgress
                                variant="determinate"
                                value={
                                  (activity.views /
                                    Math.max(
                                      ...data.activities.popular.map(
                                        (a) => a.views
                                      )
                                    )) *
                                  100
                                }
                                sx={{ width: 100, ml: "auto" }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Activity Categories
                  </Typography>
                  <Recharts.ResponsiveContainer width="100%" height={300}>
                    <Recharts.PieChart>
                      <Recharts.Pie
                        data={data.activities.categories}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, percent }: PieChartData) =>
                          `${category} ${((percent || 0) * 100).toFixed(0)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {data.activities.categories.map((entry, index) => (
                          <Recharts.Cell
                            key={`cell-${index}`}
                            fill={chartColors[index % chartColors.length]}
                          />
                        ))}
                      </Recharts.Pie>
                      <Recharts.Tooltip />
                    </Recharts.PieChart>
                  </Recharts.ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    System Performance
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Speed />
                      </ListItemIcon>
                      <ListItemText
                        primary="Average Response Time"
                        secondary={`${data.performance.avgResponseTime}ms`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <DeviceHub />
                      </ListItemIcon>
                      <ListItemText
                        primary="Uptime"
                        secondary={`${data.performance.uptime}%`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <Timeline />
                      </ListItemIcon>
                      <ListItemText
                        primary="Error Rate"
                        secondary={`${data.performance.errorRate}%`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Performance Metrics
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Response Time</Typography>
                      <Typography variant="body2" color="primary">
                        {data.performance.avgResponseTime}ms
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        (data.performance.avgResponseTime / 1000) * 100,
                        100
                      )}
                      color={
                        data.performance.avgResponseTime < 500
                          ? "success"
                          : "warning"
                      }
                    />
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Uptime</Typography>
                      <Typography variant="body2" color="primary">
                        {data.performance.uptime}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={data.performance.uptime}
                      color="success"
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Error Rate</Typography>
                      <Typography variant="body2" color="primary">
                        {data.performance.errorRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={data.performance.errorRate}
                      color={
                        data.performance.errorRate < 1 ? "success" : "error"
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Real-time Events Tab */}
        <TabPanel value={tabValue} index={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Events
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>User ID</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Properties</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.events.recentEvents.slice(0, 10).map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Chip
                            label={event.event}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{event.userId}</TableCell>
                        <TableCell>
                          {new Date(event.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {JSON.stringify(event.properties, null, 2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AnalyticsDashboard;
